import { Injectable } from '@angular/core';

/**
 * Which web-storage area a key lives in. `session` (the default) is per-tab.
 * `local` is for hand-offs that have to survive a new tab — notably the corporate
 * signup -> onboarding Company Name, since the verification email link usually
 * opens a fresh tab where sessionStorage is empty.
 */
export type StorageArea = 'session' | 'local';

@Injectable({
    providedIn: 'root',
})
export class SessionStorageService {
    private readonly isBrowser = typeof window !== 'undefined';

    read<T>(key: string, fallback: T, area: StorageArea = 'session'): T {
        if (!this.isBrowser) {
            return fallback;
        }

        const raw = this.storage(area).getItem(key);

        if (!raw) {
            return fallback;
        }

        try {
            return JSON.parse(raw) as T;
        } catch {
            return fallback;
        }
    }

    write<T>(key: string, value: T, area: StorageArea = 'session') {
        if (!this.isBrowser) {
            return;
        }

        try {
            this.storage(area).setItem(key, JSON.stringify(value));
        } catch (error) {
            console.warn(`Session storage write skipped for ${key}:`, error);
        }
    }

    remove(key: string, area: StorageArea = 'session') {
        if (!this.isBrowser) {
            return;
        }

        this.storage(area).removeItem(key);
    }

    private storage(area: StorageArea): Storage {
        return area === 'local' ? window.localStorage : window.sessionStorage;
    }
}
